import { useEffect } from "react";
import { initCrypto } from "@veto/crypto-bridge";
import type { Account } from "@veto/domain";
import { getAccount, getVault } from "@veto/storage";
import { reportSecureVaultCapabilities, type SecureVaultCapabilities } from "../secureVault";
import { APP_TOAST_EVENT } from "../appToast";

type Screen = "loading" | "onboarding" | "lock" | "app" | "error";

/** Cold-start: WASM init + route to onboarding or lock. */
export function useAppBootstrap(opts: {
  setScreen: (s: Screen) => void;
  setAccount: (a: Account | null) => void;
  setToast: (t: string | null) => void;
  setVaultCaps: (c: SecureVaultCapabilities | null) => void;
}): void {
  useEffect(() => {
    void reportSecureVaultCapabilities().then(opts.setVaultCaps);
  }, [opts.setVaultCaps]);

  // showAppToast() — e.g. "Saved to My stickers" from a message action sheet.
  const { setToast } = opts;
  useEffect(() => {
    const onToast = (e: Event) => {
      const text = (e as CustomEvent<unknown>).detail;
      if (typeof text === "string" && text) setToast(text.slice(0, 200));
    };
    window.addEventListener(APP_TOAST_EVENT, onToast);
    return () => window.removeEventListener(APP_TOAST_EVENT, onToast);
  }, [setToast]);

  useEffect(() => {
    (async () => {
      await initCrypto();
      const acc = await getAccount();
      const vault = await getVault();
      if (!acc || !vault) {
        opts.setScreen("onboarding");
        return;
      }
      opts.setAccount(acc);
      opts.setScreen("lock");
    })().catch(() => {
      // NEVER fall through to onboarding on a transient failure (blocked DB
      // upgrade, storage hiccup): creating an identity there would overwrite
      // the existing vault — permanent identity loss.
      opts.setToast("Veto couldn't open its secure storage on this device");
      opts.setScreen("error");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
